import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { extractEmails } from "./Composer";
import { cn } from "./ui/cn";

/**
 * A list of email recipients, entered as chips.
 *
 * The plain comma-separated text box this replaces was reported as "can type an
 * email in but it won't enter it as an email", and that is exactly right: there
 * was no moment at which an address became a thing rather than more text. So
 * you could not tell a finished address from a half-typed one, could not remove
 * the second of three without editing around the commas, and had no feedback
 * that a typo had produced nothing at all -- it was silently dropped at send.
 *
 * Committing on Enter, Tab, comma, semicolon and blur is what fixes that. All
 * five are what people already press: Enter and Tab because every mail client
 * accepts them, comma and semicolon because that is how a list is written by
 * hand, and blur because clicking straight into the subject line should not
 * throw away what was typed.
 */

export interface Recipient {
  email: string;
  name?: string;
}

export function RecipientInput({
  value,
  onChange,
  placeholder = "name@example.com",
  id,
  autoFocus,
  max,
}: {
  value: Recipient[];
  onChange: (next: Recipient[]) => void;
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
  /**
   * Cap on recipients. Used by the To field, which is one address: a ticket is
   * a conversation with a customer, and the ticket's contact is that person.
   * Copy anybody else in on Cc, which is what Cc is.
   */
  max?: number;
}) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  /*
   * Debounced so a lookup is not issued per keystroke. Short, because this runs
   * while somebody is waiting to see whether the address they want is offered.
   */
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(text.trim()), 150);
    return () => window.clearTimeout(timer);
  }, [text]);

  const suggestions = useQuery({
    queryKey: ["recipients", debounced],
    queryFn: () => api.recipients(debounced),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });

  const taken = new Set(value.map((entry) => entry.email.toLowerCase()));
  const options = (suggestions.data ?? []).filter(
    (option) => !taken.has(option.email.toLowerCase()),
  );

  // A stale highlight index outlives the list it pointed into.
  useEffect(() => setHighlight(0), [debounced]);

  const full = max !== undefined && value.length >= max;

  const add = (recipients: Recipient[]): void => {
    if (recipients.length === 0) return;

    const seen = new Set(value.map((entry) => entry.email.toLowerCase()));
    const additions = recipients.filter((entry) => {
      const key = entry.email.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    /*
     * At the cap, a new address replaces the one there rather than being
     * refused. Correcting a mistyped recipient is the reason somebody types a
     * second one into a field that holds one, and silently ignoring it would
     * leave the wrong address in place looking accepted.
     */
    const next =
      max !== undefined
        ? [...value, ...additions].slice(-max)
        : [...value, ...additions];

    if (additions.length) onChange(next);
    setText("");
    setOpen(false);
  };

  /**
   * Turn whatever is in the box into chips.
   *
   * Runs the same address extraction the send path uses, so anything that
   * survives here is something the server will accept -- and anything that does
   * not is left in the box, visibly unfinished, rather than vanishing.
   */
  const commitTyped = (): boolean => {
    const found = extractEmails(text);
    if (found.length === 0) return false;
    add(found.map((email) => ({ email })));
    return true;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (open && options.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlight((index) => (index + 1) % options.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlight((index) => (index - 1 + options.length) % options.length);
        return;
      }
      if (event.key === "Enter" && options[highlight]) {
        event.preventDefault();
        const option = options[highlight]!;
        add([{ email: option.email, name: option.name ?? undefined }]);
        return;
      }
    }

    if (["Enter", "Tab", ",", ";"].includes(event.key)) {
      // Tab still moves on when there is nothing to commit, so it keeps
      // working as Tab for anyone reaching the field and passing through it.
      const committed = commitTyped();
      if (committed || event.key !== "Tab") event.preventDefault();
      return;
    }

    /*
     * Backspace on an empty box removes the last chip. Standard in every mail
     * client, and the fastest way to undo the address you just committed by
     * mistake.
     */
    if (event.key === "Backspace" && text === "" && value.length > 0) {
      event.preventDefault();
      onChange(value.slice(0, -1));
      return;
    }

    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div
        onClick={() => inputRef.current?.focus()}
        className={cn(
          "flex min-h-[1.9rem] w-full flex-wrap items-center gap-1 rounded-md border border-line bg-surface px-1.5 py-1",
          "focus-within:ring-2 focus-within:ring-brand-500/40",
        )}
      >
        {value.map((recipient) => (
          <span
            key={recipient.email}
            className="flex max-w-full items-center gap-1 rounded bg-surface-2 py-0.5 pl-1.5 pr-1 text-xs text-content"
          >
            <span className="truncate" title={recipient.email}>
              {recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email}
            </span>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onChange(value.filter((entry) => entry.email !== recipient.email));
              }}
              aria-label={`Remove ${recipient.email}`}
              className="flex size-4 shrink-0 items-center justify-center rounded text-faint hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400"
            >
              {/* A real cross, and one per recipient. The single control this
                  replaces cleared the entire field. */}
              ✕
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          id={id}
          autoFocus={autoFocus}
          value={text}
          role="combobox"
          aria-expanded={open && options.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          /*
           * Committing on the way out is the whole reason a typed address is
           * not lost. The blur is deferred one tick so a click on a suggestion
           * lands before the list is torn down.
           */
          onBlur={() => {
            window.setTimeout(() => {
              commitTyped();
              setOpen(false);
            }, 120);
          }}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData("text/plain");
            const found = extractEmails(pasted);
            // Nothing address-shaped: let it paste as text so it can be fixed
            // by hand rather than silently swallowed.
            if (found.length === 0) return;
            event.preventDefault();
            add(found.map((email) => ({ email })));
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          className={cn(
            "flex-1 bg-transparent px-1 py-0.5 text-xs text-content outline-none placeholder:text-faint",
            // At the cap the box stops claiming width it has no use for, so a
            // single recipient reads as a filled field rather than a half-empty
            // one -- but it stays typeable, to replace what is there.
            full ? "min-w-[3rem]" : "min-w-[8rem]",
          )}
        />
      </div>

      {open && options.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          {options.map((option, index) => (
            <li key={option.email}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlight}
                // mousedown, not click: the input's blur would otherwise fire
                // first and close the list out from under the pointer.
                onMouseDown={(event) => {
                  event.preventDefault();
                  add([{ email: option.email, name: option.name ?? undefined }]);
                }}
                onMouseEnter={() => setHighlight(index)}
                className={cn(
                  "block w-full px-2 py-1 text-left text-xs",
                  index === highlight ? "bg-surface-2" : "hover:bg-surface-2",
                )}
              >
                <span className="block truncate text-content">{option.email}</span>
                {option.name || option.companyName ? (
                  <span className="block truncate text-[11px] text-muted">
                    {[option.name, option.companyName].filter(Boolean).join(" · ")}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
